import { create } from "zustand";
import { getCartStoreConfiguration, reportCartDiagnostic } from "../config/cartRuntimeConfig";
import { emitCartEvent } from "../events/cartEvents";
import {
    createEmptyCartSnapshot,
    CART_STORAGE_KEY,
    readCartSnapshot,
} from "../storage/cartStorage";
import type {
    AddCartItemInput,
    AddToCartOptions,
    CartItem,
    CartOperation,
    CartPersistedState,
    CartReconciliationChange,
    CartReconciliationReason,
    CartReconciliationResult,
    CartStore,
} from "../types/cart.types";
import {
    getCartItemQuantityContext,
    getValidCartQuantity,
    normalizeCartItemMetadata,
} from "../utils/cartQuantity";
import { applyCartOperations } from "./cartOperations";
import {
    commitCanonicalCartItems,
    commitCartOperations,
    replaceLegacyCartItems,
    validateAndRepairCartPersistence,
} from "./cartCoordinator";

const MAX_RECONCILIATION_RETRIES = 3;
const CART_OPEN_DEDUPE_MS = 5_000;
let activeReconciliation: Promise<CartReconciliationResult> | null = null;

const readInitialSnapshot = (): CartPersistedState => {
    if (typeof window === "undefined") return createEmptyCartSnapshot();

    try {
        return readCartSnapshot();
    } catch (error) {
        console.error("No se pudo hidratar el carrito persistido.", error);
        return createEmptyCartSnapshot();
    }
};

const initialSnapshot = readInitialSnapshot();

const isSameCartLine = (
    item: CartItem,
    identity: Pick<CartItem, "idProduct" | "idProductVariant">,
): boolean =>
    item.idProduct === identity.idProduct && item.idProductVariant === identity.idProductVariant;

const lineKey = (item: Pick<CartItem, "idProduct" | "idProductVariant">): string =>
    `${item.idProduct}\u0000${item.idProductVariant}`;

const emitUserAddedItemEvents = (
    previousItems: CartItem[],
    nextItems: CartItem[],
    operations: CartOperation[],
): void => {
    const emittedLines = new Set<string>();

    for (const operation of operations) {
        if (operation.type !== "add" || operation.source !== "USER") continue;

        const key = lineKey(operation.item);
        if (emittedLines.has(key)) continue;
        if (previousItems.some((item) => isSameCartLine(item, operation.item))) continue;

        const addedItem = nextItems.find((item) => isSameCartLine(item, operation.item));
        if (!addedItem) continue;

        emittedLines.add(key);
        emitCartEvent({ type: "ITEM_ADDED", item: addedItem });
    }
};

const inferChanges = (
    previousItems: readonly CartItem[],
    result: CartReconciliationResult,
): CartReconciliationChange[] => {
    const previousByLine = new Map(previousItems.map((item) => [lineKey(item), item]));
    const canonicalByLine = new Map(result.items.map((item) => [lineKey(item), item]));
    const inferred: CartReconciliationChange[] = [];

    for (const previous of previousItems) {
        const current = canonicalByLine.get(lineKey(previous));
        if (!current) {
            inferred.push({
                type: "REMOVED",
                productId: previous.idProduct,
                variantId: previous.idProductVariant,
                previousQuantity: previous.quantity,
                reason: "El producto ya no está disponible.",
            });
            continue;
        }

        if (previous.price !== current.price) {
            inferred.push({
                type: "PRICE_CHANGED",
                productId: previous.idProduct,
                variantId: previous.idProductVariant,
                previousPrice: previous.price,
                currentPrice: current.price,
                reason: "El precio se actualizó al valor vigente.",
            });
        }

        if (previous.quantity !== current.quantity) {
            inferred.push({
                type: "QUANTITY_ADJUSTED",
                productId: previous.idProduct,
                variantId: previous.idProductVariant,
                previousQuantity: previous.quantity,
                currentQuantity: current.quantity,
                reason: "La cantidad se ajustó a las reglas o al stock vigente.",
            });
        }
    }

    /*
     * Los cambios de backend son informativos. El aviso visible se publica
     * sólo si el snapshot solicitado y la respuesta canónica prueban que
     * realmente hubo una diferencia.
     */
    const effectiveReportedChanges = result.changes.filter((change) => {
        const key = lineKey({
            idProduct: change.productId,
            idProductVariant: change.variantId,
        });
        const previous = previousByLine.get(key);
        const current = canonicalByLine.get(key);

        switch (change.type) {
            case "PRICE_CHANGED":
                return (
                    previous !== undefined &&
                    current !== undefined &&
                    previous.price !== current.price
                );
            case "QUANTITY_ADJUSTED":
                return (
                    previous !== undefined &&
                    current !== undefined &&
                    previous.quantity !== current.quantity
                );
            case "REMOVED":
                return previous !== undefined && current === undefined;
            default:
                return false;
        }
    });

    const merged = [...effectiveReportedChanges, ...inferred];
    const seen = new Set<string>();
    return merged.filter((change) => {
        const key = `${change.type}\u0000${change.productId}\u0000${change.variantId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const describeError = (error: unknown): string =>
    error instanceof Error && error.message
        ? error.message
        : "No se pudieron verificar los precios del carrito.";

const skippedResult = (
    items: CartItem[],
    pricingContext: string | null,
): CartReconciliationResult => ({
    items,
    pricingContext: pricingContext ?? "",
    changes: [],
    changed: false,
    skipped: true,
});

const validateCanonicalResponse = (
    requestedItems: readonly CartItem[],
    result: CartReconciliationResult,
): CartReconciliationResult => {
    if (!result.pricingContext?.trim()) {
        throw new Error("La API no devolvió un contexto de precios válido.");
    }

    const requestedKeys = new Set(requestedItems.map(lineKey));
    const canonicalKeys = new Set<string>();
    const items = result.items.map((candidate) => {
        const item = normalizeCartItemMetadata(candidate);
        const key = lineKey(item);
        if (!requestedKeys.has(key)) {
            throw new Error("La API devolvió una línea ajena al carrito solicitado.");
        }
        if (canonicalKeys.has(key)) {
            throw new Error("La API devolvió líneas canónicas duplicadas.");
        }
        if (
            !Number.isFinite(item.price) ||
            item.price < 0 ||
            getValidCartQuantity(item.quantity, getCartItemQuantityContext(item)) === null
        ) {
            throw new Error("La API devolvió una línea canónica inválida.");
        }

        canonicalKeys.add(key);
        return item;
    });

    return {
        ...result,
        pricingContext: result.pricingContext.trim(),
        items,
    };
};

export const useCartStore = create<CartStore>((set, get) => ({
    items: initialSnapshot.items,
    persistedItems: initialSnapshot.items,
    revision: initialSnapshot.revision,
    isOpen: false,
    isOverrideActive: false,
    isReconciling: false,
    lastPricingContext: initialSnapshot.pricingContext ?? null,
    lastReconciledAt: initialSnapshot.reconciledAt ?? null,
    reconciliationError: null,
    reconciliationChanges: [],

    hydrateCartItems: async (items) => {
        const result = await replaceLegacyCartItems(Array.isArray(items) ? items : []);
        set({
            items: result.items,
            persistedItems: result.items,
            revision: result.revision,
            isOverrideActive: false,
            lastPricingContext: null,
            lastReconciledAt: null,
            reconciliationError: null,
            reconciliationChanges: [],
        });
    },

    beginCartOverride: (items) => {
        set({
            items: Array.isArray(items) ? items : [],
            isOverrideActive: true,
            lastPricingContext: null,
            lastReconciledAt: null,
            reconciliationError: null,
            reconciliationChanges: [],
        });
    },

    endCartOverride: () => {
        let latestSnapshot: CartPersistedState;
        try {
            latestSnapshot = readCartSnapshot();
        } catch (error) {
            console.error(
                "No se pudo restaurar el carrito persistido al finalizar el override.",
                error,
            );
            latestSnapshot = {
                items: get().persistedItems,
                revision: get().revision,
                updatedAt: new Date().toISOString(),
            };
        }

        set({
            items: latestSnapshot.items,
            persistedItems: latestSnapshot.items,
            revision: latestSnapshot.revision,
            isOverrideActive: false,
            lastPricingContext: latestSnapshot.pricingContext ?? null,
            lastReconciledAt: latestSnapshot.reconciledAt ?? null,
            reconciliationError: null,
            reconciliationChanges: [],
        });
    },

    applyOperations: async (operations) => {
        if (operations.length === 0) return get().items;

        if (get().isOverrideActive) {
            const previousItems = get().items;
            const nextItems = applyCartOperations(previousItems, operations);
            set({
                items: nextItems,
                lastPricingContext: null,
                lastReconciledAt: null,
                reconciliationError: null,
                reconciliationChanges: [],
            });
            emitUserAddedItemEvents(previousItems, nextItems, operations);
            return nextItems;
        }

        const previousItems = get().persistedItems;
        const result = await commitCartOperations(operations, previousItems);
        set((state) => {
            const verificationState = result.changed
                ? {
                      lastPricingContext: null,
                      lastReconciledAt: null,
                      reconciliationError: null,
                      reconciliationChanges: [],
                  }
                : {};

            return state.isOverrideActive
                ? {
                      persistedItems: result.items,
                      revision: result.revision,
                      ...verificationState,
                  }
                : {
                      items: result.items,
                      persistedItems: result.items,
                      revision: result.revision,
                      ...verificationState,
                  };
        });

        emitUserAddedItemEvents(previousItems, result.items, operations);

        return get().items;
    },

    validateAndRepairCart: async () => {
        const snapshot = await validateAndRepairCartPersistence();
        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                      lastPricingContext: snapshot.pricingContext ?? null,
                      lastReconciledAt: snapshot.reconciledAt ?? null,
                  },
        );

        return get().items;
    },

    reconcilePrices: async (reason: CartReconciliationReason) => {
        if (activeReconciliation) return activeReconciliation;

        const current = get();
        const reconcileCart = getCartStoreConfiguration().reconcileCart;
        if (!reconcileCart) {
            return skippedResult(current.items, current.lastPricingContext);
        }

        if (
            reason === "CART_OPEN" &&
            current.lastPricingContext &&
            current.lastReconciledAt &&
            Date.now() - Date.parse(current.lastReconciledAt) < CART_OPEN_DEDUPE_MS &&
            !current.reconciliationError
        ) {
            return skippedResult(current.items, current.lastPricingContext);
        }

        activeReconciliation = (async () => {
            set({
                isReconciling: true,
                reconciliationError: null,
            });

            try {
                if (get().isOverrideActive) {
                    const previousItems = get().items;
                    if (previousItems.length === 0) {
                        const result = skippedResult(previousItems, null);
                        set({
                            isReconciling: false,
                            lastPricingContext: null,
                            lastReconciledAt: new Date().toISOString(),
                            reconciliationChanges: [],
                        });
                        return result;
                    }

                    const response = validateCanonicalResponse(
                        previousItems,
                        await reconcileCart(previousItems),
                    );
                    const changes = inferChanges(previousItems, response);
                    const reconciledAt = new Date().toISOString();
                    set({
                        items: response.items,
                        isReconciling: false,
                        lastPricingContext: response.pricingContext,
                        lastReconciledAt: reconciledAt,
                        reconciliationChanges: changes,
                    });
                    return {
                        ...response,
                        changes,
                        changed: JSON.stringify(previousItems) !== JSON.stringify(response.items),
                    };
                }

                for (let attempt = 1; attempt <= MAX_RECONCILIATION_RETRIES; attempt++) {
                    const snapshot = await validateAndRepairCartPersistence();
                    if (snapshot.items.length === 0) {
                        const result = skippedResult(snapshot.items, null);
                        set({
                            items: [],
                            persistedItems: [],
                            revision: snapshot.revision,
                            isReconciling: false,
                            lastPricingContext: null,
                            lastReconciledAt: new Date().toISOString(),
                            reconciliationChanges: [],
                        });
                        return result;
                    }

                    const response = validateCanonicalResponse(
                        snapshot.items,
                        await reconcileCart(snapshot.items),
                    );
                    const changes = inferChanges(snapshot.items, response);
                    const commit = await commitCanonicalCartItems(
                        snapshot.revision,
                        response.items,
                        response.pricingContext,
                    );

                    if (!commit.revisionMatched) continue;

                    set((state) =>
                        state.isOverrideActive
                            ? {
                                  persistedItems: commit.items,
                                  revision: commit.revision,
                                  isReconciling: false,
                                  lastPricingContext: response.pricingContext,
                                  lastReconciledAt: commit.reconciledAt ?? new Date().toISOString(),
                                  reconciliationChanges: changes,
                              }
                            : {
                                  items: commit.items,
                                  persistedItems: commit.items,
                                  revision: commit.revision,
                                  isReconciling: false,
                                  lastPricingContext: response.pricingContext,
                                  lastReconciledAt: commit.reconciledAt ?? new Date().toISOString(),
                                  reconciliationChanges: changes,
                              },
                    );

                    return {
                        ...response,
                        items: commit.items,
                        changes,
                        changed: commit.changed,
                    };
                }

                const conflictError = new Error(
                    "El carrito cambió mientras se verificaban sus precios. Volvé a intentar.",
                );
                reportCartDiagnostic({
                    kind: "RECONCILIATION_CONFLICT_EXHAUSTED",
                    timestampUtc: new Date().toISOString(),
                    error: conflictError,
                    context: { reason, retries: MAX_RECONCILIATION_RETRIES },
                });
                throw conflictError;
            } catch (error) {
                const message = describeError(error);
                set({
                    isReconciling: false,
                    reconciliationError: message,
                    lastPricingContext: null,
                    lastReconciledAt: null,
                });
                reportCartDiagnostic({
                    kind: "RECONCILIATION_FAILED",
                    timestampUtc: new Date().toISOString(),
                    error,
                    context: {
                        reason,
                        revision: get().revision,
                        itemCount: get().items.length,
                    },
                });
                throw error;
            } finally {
                activeReconciliation = null;
            }
        })();

        return activeReconciliation;
    },

    applyExternalSnapshot: (snapshot) => {
        if (snapshot.revision <= get().revision) return;

        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                      lastPricingContext: snapshot.pricingContext ?? null,
                      lastReconciledAt: snapshot.reconciledAt ?? null,
                      reconciliationError: null,
                      reconciliationChanges: [],
                  },
        );
    },

    addToCart: async (item: AddCartItemInput, options?: AddToCartOptions) => {
        await get().applyOperations([{ type: "add", item, source: options?.source ?? "USER" }]);
    },

    removeFromCart: async (idProduct, idProductVariant) => {
        await get().applyOperations([{ type: "remove", idProduct, idProductVariant }]);
    },

    updateQuantity: async (idProduct, idProductVariant, quantity) => {
        await get().applyOperations([
            { type: "updateQuantity", idProduct, idProductVariant, quantity },
        ]);
    },

    clearCart: async () => {
        await get().applyOperations([{ type: "clear" }]);
        set({ isOpen: false });
    },

    openCart: () => {
        if (get().isOpen) return;

        set({ isOpen: true });
        void get()
            .validateAndRepairCart()
            .then(() => get().reconcilePrices("CART_OPEN"))
            .catch((error) => console.error("No se pudo verificar el carrito al abrirlo.", error));
    },
    closeCart: () => set({ isOpen: false }),
    toggleCart: () => {
        const willOpen = !get().isOpen;
        set({ isOpen: willOpen });
        if (!willOpen) return;

        void get()
            .validateAndRepairCart()
            .then(() => get().reconcilePrices("CART_OPEN"))
            .catch((error) => console.error("No se pudo verificar el carrito al abrirlo.", error));
    },
}));

const handleStorageChange = (event: StorageEvent): void => {
    if (event.key !== CART_STORAGE_KEY) return;

    try {
        useCartStore.getState().applyExternalSnapshot(readCartSnapshot());
    } catch (error) {
        console.error("No se pudo sincronizar el carrito desde otra pestaña.", error);
    }
};

if (typeof window !== "undefined") {
    const cartWindow = window as typeof window & {
        __ecommerceCartStorageListener?: (event: StorageEvent) => void;
    };

    if (cartWindow.__ecommerceCartStorageListener) {
        window.removeEventListener("storage", cartWindow.__ecommerceCartStorageListener);
    }

    window.addEventListener("storage", handleStorageChange);
    cartWindow.__ecommerceCartStorageListener = handleStorageChange;
}
